
import React, { useState, useEffect, useMemo } from 'react';
import { useStudents } from '../hooks/useStudents';
import { theme } from '../utils/theme';
import { saveMessageTemplate, getAllMessageTemplates, deleteMessageTemplate, uploadTemplateMedia } from '../firebase/database';

const C = theme.colors;

const WhatsAppSender = () => {
  const { students, loading: studentsLoading } = useStudents();
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState('');
  const [filecaption, setFilecaption] = useState('');
  const [speed, setSpeed] = useState('normal');
  const [mediaFile, setMediaFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Template states
  const [templates, setTemplates] = useState([]);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await getAllMessageTemplates();
      setTemplates(data);
    } catch (err) {
      console.error("Error loading templates:", err);
    }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return alert("Enter a template name");
    
    setLoading(true);
    try {
      let mediaBase64 = null;
      if (mediaFile) {
        // Size check: 1MB = 1048576 bytes
        if (mediaFile.size > 1048576) {
          throw new Error("File too large! Max 1MB allowed for database storage.");
        }
        mediaBase64 = await fileToBase64(mediaFile);
      }

      await saveMessageTemplate(newTemplateName, {
        selectedIds,
        message,
        filecaption,
        speed,
        mediaBase64, // Save the actual image data in database
        mediaFileName: mediaFile?.name || ''
      });
      
      setShowSaveModal(false);
      setNewTemplateName('');
      await loadTemplates();
      alert("Template saved successfully with image!");
    } catch (err) {
      alert("Error saving template: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = async (tpl) => {
    setLoading(true);
    try {
      setSelectedIds(tpl.selectedIds || []);
      setMessage(tpl.message || '');
      setFilecaption(tpl.filecaption || '');
      setSpeed(tpl.speed || 'normal');
      
      if (tpl.mediaBase64) {
        try {
          // Convert Base64 back to a File object
          const res = await fetch(tpl.mediaBase64);
          const blob = await res.blob();
          const file = new File([blob], tpl.mediaFileName || "template_image.jpg", { type: blob.type });
          setMediaFile(file);
        } catch (e) {
          console.error("Error restoring media:", e);
          setMediaFile(null);
        }
      } else {
        setMediaFile(null);
      }
      
      setShowLoadModal(false);
      alert(`Template "${tpl.name}" loaded fully!`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (e, tpl) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await deleteMessageTemplate(tpl.id);
      await loadTemplates();
    } catch (err) {
      alert("Error deleting template");
    }
  };

  // Derived numbers from selected students
  const numbers = useMemo(() => {
    return selectedIds
      .map(id => students.find(s => s.id === id)?.phone)
      .filter(phone => phone)
      .join(', ');
  }, [selectedIds, students]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students;
    return students.filter(s => 
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (s.roll && s.roll.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [students, searchTerm]);

  const toggleStudent = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    const activeIds = filteredStudents.filter(s => s.status !== 'inactive').map(s => s.id);
    setSelectedIds(activeIds);
  };

  const clearSelection = () => setSelectedIds([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!numbers) {
      alert("Please select at least one person with a phone number.");
      return;
    }
    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('numbers', numbers);
    formData.append('message', message);
    formData.append('filecaption', filecaption);
    formData.append('speed', speed);
    
    if (mediaFile) {
      formData.append('mediaFile', mediaFile);
    }

    try {
      const response = await fetch('http://Sonias-MacBook-Air.local:3001/send', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setResult({
          success: true,
          mode: data.mode,
          sent: data.sent,
          total: data.total,
          message: `✅ Sent ${data.sent}/${data.total} messages (${data.mode})`
        });
      } else {
        setResult({ success: false, message: data.error || 'Server error' });
      }
      
    } catch (error) {
      setResult({ 
        success: false, 
        message: `❌ Error: ${error.message}. Make sure your WhatsApp server is running at localhost:3001` 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 0 40px 0', fontFamily: theme.fonts.sans }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text }}>WhatsApp Bulk Sender</h2>
          <p style={{ fontSize: 14, color: C.muted }}>Send messages and media via your local WhatsApp gateway</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button 
            onClick={() => setShowLoadModal(true)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            📂 Load Template
          </button>
          <button 
            onClick={() => setShowSaveModal(true)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accentSoft, color: C.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            💾 Save Template
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 24 }}>
        {/* Left: Selection */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', height: 'fit-content' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Select Persons</h3>
            <span style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>{selectedIds.length} selected</span>
          </div>

          <input 
            type="text" 
            placeholder="Search name or roll..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, outline: 'none', fontSize: 14 }}
          />

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={selectAll} style={{ flex: 1, padding: '6px', fontSize: 12, borderRadius: 6, border: `1px solid ${C.accent}`, background: 'none', color: C.accent, cursor: 'pointer', fontWeight: 600 }}>Select All</button>
            <button onClick={clearSelection} style={{ flex: 1, padding: '6px', fontSize: 12, borderRadius: 6, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer', fontWeight: 600 }}>Clear</button>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8, padding: 4 }}>
            {studentsLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 13 }}>Loading persons...</div>
            ) : filteredStudents.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 13 }}>No persons found</div>
            ) : (
              filteredStudents.map(s => (
                <div 
                  key={s.id} 
                  onClick={() => toggleStudent(s.id)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 10, 
                    padding: '8px 10px', 
                    borderRadius: 6, 
                    cursor: 'pointer',
                    background: selectedIds.includes(s.id) ? C.accentSoft : 'transparent',
                    marginBottom: 2,
                    transition: '0.2s'
                  }}
                >
                  <input 
                    type="checkbox" 
                    checked={selectedIds.includes(s.id)} 
                    onChange={() => {}} // Handled by div onClick
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: selectedIds.includes(s.id) ? C.accent : C.text }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{s.roll ? `#${s.roll} · ` : ''}{s.phone || 'No phone'}</div>
                  </div>
                  {s.role === 'staff' && <span style={{ fontSize: 10, background: C.purpleSoft, color: C.purple, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>Staff</span>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Form */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>📱 Recipient Numbers</label>
              <textarea
                placeholder="Numbers will appear here as you select persons..."
                value={numbers}
                readOnly
                rows="3"
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#F9FAFB', fontSize: 13, color: C.text, outline: 'none', resize: 'vertical' }}
              />
              <small style={{ fontSize: 11, color: C.muted, marginTop: 4, display: 'block' }}>Automatically pulled from selected profiles</small>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>💬 Message Content</label>
              <textarea
                placeholder="Hello {name}! This is a test message."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows="4"
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, outline: 'none', fontFamily: 'inherit' }}
                required
              />
              <small style={{ fontSize: 11, color: C.muted, marginTop: 4, display: 'block' }}>Use <code>{'{name}'}</code> to personalize for each contact</small>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>📁 Media File</label>
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => {
                    setMediaFile(e.target.files[0]);
                  }}
                  style={{ fontSize: 12, width: '100%' }}
                />
                {mediaFile && (
                  <div style={{ marginTop: 4, fontSize: 11, color: C.accent, fontWeight: 600 }}>
                    Selected: {mediaFile.name}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>📄 File Caption</label>
                <input
                  type="text"
                  placeholder="Optional caption"
                  value={filecaption}
                  onChange={(e) => setFilecaption(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>⚡ Sending Speed</label>
              <select 
                value={speed} 
                onChange={(e) => setSpeed(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, outline: 'none', background: '#fff' }}
              >
                <option value="high">High (12-35s delay)</option>
                <option value="normal">Normal (30-70s delay)</option>
                <option value="slow">Slow (60-150s delay)</option>
              </select>
            </div>

            <button 
              type="submit" 
              disabled={loading || selectedIds.length === 0}
              style={{
                width: '100%', 
                padding: '14px', 
                background: loading ? C.dim : C.green,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 700,
                cursor: loading || selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : `0 4px 12px ${C.green}44`,
                transition: '0.2s'
              }}
            >
              {loading ? '🚀 Sending...' : `🚀 Send WhatsApp to ${selectedIds.length} Persons`}
            </button>
          </form>

          {result && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              borderRadius: '10px',
              background: result.success ? C.greenSoft : C.redSoft,
              border: `1px solid ${result.success ? C.green + '44' : C.red + '44'}`,
              color: result.success ? C.green : C.red,
              fontSize: 14
            }}>
              <strong style={{ display: 'block', marginBottom: 4 }}>{result.success ? '✅ Success!' : '❌ Error:'}</strong>
              {result.message}
            </div>
          )}
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <h3 style={{ marginBottom: 16, fontSize: 18 }}>Save as Template</h3>
            <input 
              type="text" 
              placeholder="Template name (e.g. Holiday Greeting)" 
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 20, fontSize: 14 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowSaveModal(false)} style={{ flex: 1, padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveTemplate} disabled={loading} style={{ flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, margin: 0 }}>My Templates</h3>
              <button onClick={() => setShowLoadModal(false)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>&times;</button>
            </div>
            
            {templates.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>No templates saved yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {templates.map(t => (
                  <div 
                    key={t.id} 
                    onClick={() => applyTemplate(t)}
                    style={{ padding: '14px', borderRadius: 10, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: '0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{t.selectedIds?.length || 0} recipients · Speed: {t.speed}</div>
                    </div>
                    <button 
                      onClick={(e) => handleDeleteTemplate(e, t)}
                      style={{ padding: '6px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSender;
